/**
 * Local-only state for the guest study flow.
 *
 * The flow itself lives inside the existing app:
 *   1. Anon user arrives and the FingerprintJS flow creates an anon session.
 *   2. They browse Learn (`/home/roadmap`) and optionally start a track.
 *   3. On session-complete, the top soft-gate banner prompts them to sign up
 *      so the review schedule survives the next visit.
 *
 * This module is the cheap localStorage side-channel used to (a) one-shot
 * `demo_started` analytics per surface, and (b) drive the soft-gate banner
 * shown after a guest finishes a session.
 *
 * Server-truth for everything user-visible (decks, progress, EXP) lives in
 * Firestore; nothing in this file is authoritative.
 */

const KEY_AUTO_STUDY_LAUNCHED = 'fc:guestAutoStudyLaunchedAt';
const KEY_DEMO_STARTED_SURFACES = 'fc:guestDemoStartedSurfaces';
const KEY_STUDY_COMPLETED = 'fc:guestStudyCompletedAt';
const KEY_SAVE_DISMISSED = 'fc:guestSaveDismissedAt';
const KEY_FIRST_NEXT_REVIEW = 'fc:guestFirstNextReview';
const KEY_BANNER_SNOOZED_SESSION = 'fc:guestSaveBannerSnoozed';

const safeStorage = {
  get(key) {
    try {
      if (typeof window === 'undefined') return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === 'undefined') return;
      if (value == null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, String(value));
    } catch {
      /* private mode / quota — ignore */
    }
  },
};

const safeSessionStorage = {
  get(key) {
    try {
      if (typeof window === 'undefined') return null;
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === 'undefined') return;
      if (value == null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, String(value));
    } catch {
      /* ignore */
    }
  },
};

function readDemoStartedSurfaces() {
  const raw = safeStorage.get(KEY_DEMO_STARTED_SURFACES);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * One-shot per analytics surface (e.g. `learn-catalog`, `track-study`).
 * @param {string} surface
 * @returns {boolean} true if this is the first time this surface was marked
 */
export function markGuestDemoStarted(surface) {
  const key = String(surface || '').trim();
  if (!key) return false;
  const surfaces = readDemoStartedSurfaces();
  if (surfaces.includes(key)) return false;
  surfaces.push(key);
  safeStorage.set(KEY_DEMO_STARTED_SURFACES, JSON.stringify(surfaces));
  return true;
}

/** True once the guest has finished at least one Study session. */
export function hasCompletedGuestStudy() {
  return Boolean(safeStorage.get(KEY_STUDY_COMPLETED));
}

export function markGuestStudyCompleted({ firstNextReviewIso } = {}) {
  safeStorage.set(KEY_STUDY_COMPLETED, new Date().toISOString());
  if (firstNextReviewIso) safeStorage.set(KEY_FIRST_NEXT_REVIEW, firstNextReviewIso);
}

/** True if the user dismissed the save modal without signing up. */
export function hasDismissedSaveModal() {
  return Boolean(safeStorage.get(KEY_SAVE_DISMISSED));
}

export function markSaveModalDismissed() {
  safeStorage.set(KEY_SAVE_DISMISSED, new Date().toISOString());
}

export function getGuestFirstNextReviewIso() {
  return safeStorage.get(KEY_FIRST_NEXT_REVIEW);
}

/**
 * True when the guest finished a session and has not signed up yet — i.e.
 * the soft-gate banner should be shown (until they snooze it or save).
 */
export function hasUnsavedGuestProgress() {
  return hasCompletedGuestStudy();
}

/**
 * True once the very first scheduled review of the demo session is due —
 * triggers the "stronger second bite" copy on the soft-gate banner.
 */
export function isSecondBiteReady(now = new Date()) {
  const iso = safeStorage.get(KEY_FIRST_NEXT_REVIEW);
  if (!iso) return false;
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return false;
  return now >= due;
}

/**
 * Snooze the soft-gate banner for the current browser session. Lets a
 * decliner keep browsing decks; on the next session the banner returns,
 * possibly with the second-bite copy if scheduled reviews are now due.
 */
export function snoozeSaveBannerForSession() {
  safeSessionStorage.set(KEY_BANNER_SNOOZED_SESSION, '1');
}

export function isSaveBannerSnoozedForSession() {
  return Boolean(safeSessionStorage.get(KEY_BANNER_SNOOZED_SESSION));
}

/**
 * Clear all guest-study tracking. Called after a successful merge/save so a
 * brand-new device or returning signed-in user doesn't see any residual
 * banners.
 */
export function clearGuestStudyState() {
  safeStorage.set(KEY_AUTO_STUDY_LAUNCHED, null);
  safeStorage.set(KEY_DEMO_STARTED_SURFACES, null);
  safeStorage.set(KEY_STUDY_COMPLETED, null);
  safeStorage.set(KEY_SAVE_DISMISSED, null);
  safeStorage.set(KEY_FIRST_NEXT_REVIEW, null);
  safeSessionStorage.set(KEY_BANNER_SNOOZED_SESSION, null);
}

/**
 * Human-readable due label for GuestSaveModal / soft-gate copy.
 * Returns tokens like `tomorrow`, `today`, `in 3 days`, or `on 6/30/2026`.
 */
export function formatGuestDueLabel(nextReviewIso) {
  if (!nextReviewIso) return 'tomorrow';
  const date = new Date(nextReviewIso);
  if (Number.isNaN(date.getTime())) return 'tomorrow';
  const now = new Date();
  const daysDiff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  if (daysDiff <= 0) return 'today';
  if (daysDiff === 1) return 'tomorrow';
  if (daysDiff <= 7) return `in ${daysDiff} days`;
  return `on ${date.toLocaleDateString()}`;
}
